/**
 * Technology Domains List Route
 *
 * Displays searchable list of technology domains
 */

import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useListPageState } from '../../hooks/useListPageState';
import { useTechnologyDomains } from '../../graphql/queries';
import {
  DataTable,
  EmptyState,
  Pagination,
  PermissionGate,
  TableSkeleton,
  type DataTableColumn,
} from '../../components/shared';
import type { TechnologyDomain } from '../../graphql/types';
import '@tylertech/forge/text-field';
import '@tylertech/forge/select';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/technology-domains/')({
  component: TechnologyDomainsListPage,
  validateSearch: (search: Record<string, unknown>) => ({
    page: Math.max(1, Number(search.page) || 1),
    pageSize: Number(search.pageSize) || 25,
  }),
});

// Export component for testing
export { TechnologyDomainsListPage };

interface TechnologyDomainFilters {
  search: string;
}

const ForgeButton = 'forge-button' as React.ElementType;

function TechnologyDomainsListPage(): JSX.Element {
  const navigate = useNavigate();
  const { page, pageSize } = Route.useSearch();

  // State for filters
  const [filters, setFilters] = useState<TechnologyDomainFilters>({
    search: '',
  });

  // Fetch technology domains with GraphQL
  const { data, loading, error } = useTechnologyDomains();

  // Extract technology domains from connection
  const technologyDomains = data?.technologyDomains?.edges?.map((edge) => edge.node) || [];

  const { sortColumn, sortDirection, handleSort, pagedItems, totalCount } =
    useListPageState<TechnologyDomain>({
      items: technologyDomains,
      page,
      pageSize,
      filterFn: (technologyDomain) => {
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          const matchesName = technologyDomain.name.toLowerCase().includes(searchLower);
          const matchesDescription = technologyDomain.description
            ?.toLowerCase()
            .includes(searchLower);
          if (!matchesName && !matchesDescription) {
            return false;
          }
        }
        return true;
      },
      sortValue: (technologyDomain, column) =>
        String(technologyDomain[column as keyof TechnologyDomain] ?? ''),
    });

  // DataTable columns
  const columns: DataTableColumn<TechnologyDomain>[] = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      width: '55%',
      render: (technologyDomain) => (
        <div>
          <div className="entity-list-name-cell">{technologyDomain.name}</div>
          {technologyDomain.description && (
            <div className="entity-list-description-cell">
              {technologyDomain.description.substring(0, 60)}
              {technologyDomain.description.length > 60 ? '...' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'tags',
      label: 'Tags',
      sortable: false,
      width: '45%',
      render: (technologyDomain) => (
        <span>
          {technologyDomain.tags && technologyDomain.tags.length > 0
            ? technologyDomain.tags.join(', ')
            : '—'}
        </span>
      ),
    },
  ];

  const handleRowClick = (technologyDomain: TechnologyDomain): void => {
    navigate({ to: `/technology-domains/${technologyDomain.entityId}` });
  };

  const handleFilterChange = (newFilters: Partial<TechnologyDomainFilters>): void => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    void navigate({ to: '/technology-domains', search: { page: 1, pageSize } });
  };

  const handleClearFilters = (): void => {
    setFilters({ search: '' });
    void navigate({ to: '/technology-domains', search: { page: 1, pageSize } });
  };

  const handlePageChange = (newPage: number): void => {
    void navigate({ to: '/technology-domains', search: { page: newPage, pageSize } });
  };

  const handlePageSizeChange = (newPageSize: number): void => {
    void navigate({ to: '/technology-domains', search: { page: 1, pageSize: newPageSize } });
  };

  if (error) {
    return (
      <div className="technology-domains-list-page">
        <h1 className="forge-typography--headline4">Technology Domains</h1>
        <div className="entity-list-error-state">
          <EmptyState
            icon="error"
            title="Failed to load technology domains"
            description={error.message}
            actionLabel="Retry"
            onAction={() => window.location.reload()}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="technology-domains-list-page">
      <div className="technology-domains-header">
        <div>
          <h1 className="forge-typography--headline4">Technology Domains</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {loading ? 'Loading...' : `${totalCount} technology domains`}
          </p>
        </div>
        <PermissionGate permission="technology-domain:create">
          <ForgeButton
            type="button"
            variant="raised"
            onClick={() => navigate({ to: '/technology-domains/new' })}
          >
            Create Technology Domain
          </ForgeButton>
        </PermissionGate>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-field">
          <input
            type="text"
            className="search-input"
            placeholder="Search technology domains..."
            aria-label="Search technology domains"
            value={filters.search}
            onChange={(e) => handleFilterChange({ search: e.target.value })}
          />
        </div>

        {filters.search && (
          <ForgeButton type="button" variant="outlined" onClick={handleClearFilters}>
            Clear Filters
          </ForgeButton>
        )}
      </div>

      {/* Data Table */}
      <div className="technology-domains-table-container">
        {loading ? (
          <TableSkeleton rows={10} columns={2} />
        ) : (
          <>
            <DataTable
              columns={columns}
              data={pagedItems}
              getRowKey={(technologyDomain) => technologyDomain.entityId}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              onRowClick={handleRowClick}
              emptyState={{
                icon: 'inbox',
                title: 'No technology domains found',
                description: filters.search
                  ? 'Try adjusting your filters'
                  : 'Create your first technology domain to get started',
                actionLabel: 'Create Technology Domain',
                onAction: () => navigate({ to: '/technology-domains/new' }),
              }}
            />
            <Pagination
              totalCount={totalCount}
              pageSize={pageSize}
              currentPage={page}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </div>
    </div>
  );
}
